"use client";

/**
 * useUploadPricing — cost calculation, balance check, subscription check.
 */

import { useCallback } from "react";
import { useSubscription } from "@/features/subscription/queries";
import { useBalance } from "@/features/subscription/payment-queries";
import { SubscriptionPlanId } from "@/features/subscription/types";

/** Minimal item shape needed by calculatePricing. Both UploadFileItem and UrlImportItem satisfy this structurally. */
export interface PricingItem {
  mediaCategory: "audio" | "video" | "text";
  amount: number;
  duration: number;
}

export interface PricingSummary {
  totalAmount: number;
  displayAmount: number;
  hasFreeMinutes: boolean;
  hasFreeTextNotes: boolean;
  isDeductFromBalance: boolean;
  canUpload: boolean;
  /** If true, subscription is expired — show paywall */
  isExpired: boolean;
  currency: string;
  /** Cohort context for free-minutes badge copy in the confirm dialog */
  freeMinutesContext: "trial" | "plan" | "none";
  /** floor(subscription.remainingSeconds / 60); 0 when absent */
  availableMinutes: number;
  freeCharacters: number;
  hasFreeCharacters: boolean;
  /** True when subscription is the active free trial (planId === Speak_Free_Trial && !isExpired). */
  isFreePlan: boolean;
}

export function useUploadPricing() {
  // Shared hook so all consumers reuse one GET /subscription cache entry.
  const {
    data: subscription,
    isLoading: isSubscriptionLoading,
  } = useSubscription();

  // Reuses the canonical useBalance hook so all consumers share a single
  // TanStack cache entry and avoid the staleTime race that existed when this
  // hook registered its own query.
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    refetch: refetchBalance,
  } = useBalance();

  const isLoading = isSubscriptionLoading || isBalanceLoading;
  const isFreePlan =
    subscription?.planId === SubscriptionPlanId.Speak_Free_Trial && !subscription?.isExpired;
  const insightCost = subscription?.cost?.insights ?? 0;
  const currentBalance = balanceData?.balance ?? 0;

  const calculatePricing = useCallback(
    (
      items: PricingItem[],
      isCompanyDomain: boolean = false
    ): PricingSummary => {
      const freeCharacters = subscription?.freeCharacters ?? 0;
      const hasFreeCharacters = freeCharacters > 0;

      if (!subscription) {
        return {
          totalAmount: 0,
          displayAmount: 0,
          hasFreeMinutes: false,
          hasFreeTextNotes: false,
          isDeductFromBalance: false,
          canUpload: false,
          isExpired: false,
          currency: "USD",
          freeMinutesContext: "none" as const,
          availableMinutes: 0,
          freeCharacters,
          hasFreeCharacters,
          isFreePlan: false,
        };
      }

      if (subscription.isExpired) {
        return {
          totalAmount: 0,
          displayAmount: 0,
          hasFreeMinutes: false,
          hasFreeTextNotes: false,
          isDeductFromBalance: false,
          canUpload: false,
          isExpired: true,
          currency: "USD",
          freeMinutesContext: "none" as const,
          availableMinutes: Math.floor((subscription?.remainingSeconds ?? 0) / 60),
          freeCharacters,
          hasFreeCharacters,
          isFreePlan: false,
        };
      }

      let totalAmount = 0;
      let displayAmount = 0;
      let hasFreeMinutes = false;
      let hasFreeTextNotes = false;
      let isDeductFromBalance = false;

      const audioVideoFiles = items.filter((f) => f.mediaCategory !== "text");
      const textFiles = items.filter((f) => f.mediaCategory === "text");

      // Audio/video pricing
      if (audioVideoFiles.length > 0) {
        const sum = audioVideoFiles.reduce((acc, f) => acc + f.amount, 0);
        const duration = audioVideoFiles.reduce(
          (acc, f) => acc + f.duration,
          0
        );
        const remainingSeconds = subscription.remainingSeconds || 0;
        const freeMinutesInSec = subscription.freeMinutes * 60;

        const isFreeUpload =
          isCompanyDomain &&
          freeMinutesInSec >= duration &&
          audioVideoFiles.length === 1 &&
          subscription.isFree &&
          subscription.planId === SubscriptionPlanId.Speak_Free_Trial &&
          duration < 2 * 60 * 60;

        if (isFreeUpload) {
          hasFreeMinutes = true;
        } else {
          hasFreeMinutes = remainingSeconds >= duration;
        }

        if (!hasFreeMinutes) {
          if (currentBalance >= parseFloat(sum.toFixed(2))) {
            isDeductFromBalance = true;
            totalAmount = parseFloat(sum.toFixed(2));
            displayAmount = totalAmount;
          } else if (remainingSeconds > 0) {
            const remainingDuration = duration - remainingSeconds;
            const remainingCost =
              insightCost * (Math.max(remainingDuration, 15) / 60);
            totalAmount = parseFloat(remainingCost.toFixed(2));
            displayAmount = totalAmount;
          } else {
            totalAmount = parseFloat(sum.toFixed(2));
            displayAmount = totalAmount;
          }
        }
      }

      // Text pricing
      if (textFiles.length > 0) {
        const isFirstUpload =
          isCompanyDomain &&
          (subscription.notes?.totalNotes ?? 0) <= 0 &&
          subscription.isFree &&
          subscription.planId === SubscriptionPlanId.Speak_Free_Trial;

        if (isFirstUpload) {
          hasFreeTextNotes = true;
        }

        const textSum = textFiles.reduce((acc, f) => acc + f.amount, 0);
        const remainingNotes = subscription.notes?.remainingNotes ?? 0;

        if (!hasFreeTextNotes && remainingNotes < textFiles.length) {
          totalAmount += parseFloat(textSum.toFixed(2));
          displayAmount = parseFloat(totalAmount.toFixed(2));
        }
      }

      displayAmount = parseFloat(displayAmount.toFixed(2));

      const freeMinutesContext: "trial" | "plan" | "none" =
        hasFreeMinutes
          ? (isFreePlan && !subscription.isExpired ? "trial" : "plan")
          : "none";

      return {
        totalAmount: parseFloat(totalAmount.toFixed(2)),
        displayAmount,
        hasFreeMinutes,
        hasFreeTextNotes,
        isDeductFromBalance,
        canUpload: true,
        isExpired: false,
        currency: "USD",
        freeMinutesContext,
        availableMinutes: Math.floor((subscription?.remainingSeconds ?? 0) / 60),
        freeCharacters,
        hasFreeCharacters,
        isFreePlan:
          subscription.planId === SubscriptionPlanId.Speak_Free_Trial &&
          !subscription.isExpired,
      };
    },
    [subscription, currentBalance, insightCost]
  );

  return {
    subscription,
    currentBalance,
    isLoading,
    isFreePlan,
    insightCost,
    calculatePricing,
    refetchBalance,
  };
}
